// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// CareRx Plan of Care — 14-step wizard definition.
// AUTO-DERIVED to match the ASP POT form EXACTLY (public/index.html): same 14 steps,
// same subsections, same fields/labels/types/options. 133 fields total. Field `key`
// is the POT field id so the payload maps 1:1 to the source form.

export type FieldType = 'text' | 'textarea' | 'date' | 'time' | 'number' | 'tel' | 'select' | 'checkbox'

export interface WizardField {
  key: string
  label: string
  type: FieldType
  options?: string[]
  sub?: string
}

export interface WizardStep { key: string; title: string; fields: WizardField[] }

export const PLAN_OF_CARE_STEPS: WizardStep[] = [
  {
    key: 'patient_certification', title: 'Patient & Certification',
    fields: [
      { key: 'ptFirst', label: 'First name', type: 'text', sub: 'Patient Demographics' },
      { key: 'ptLast', label: 'Last name', type: 'text', sub: 'Patient Demographics' },
      { key: 'ptDOB', label: 'Date of birth', type: 'date', sub: 'Patient Demographics' },
      { key: 'ptPhone', label: 'Phone', type: 'tel', sub: 'Patient Demographics' },
      { key: 'ptGender', label: 'Gender', type: 'select', options: ['Select', 'Male', 'Female', 'Non-binary', 'Prefer not to say'], sub: 'Patient Demographics' },
      { key: 'ptAddr', label: 'Home address', type: 'text', sub: 'Patient Demographics' },
      { key: 'ptEC', label: 'Emergency contact', type: 'text', sub: 'Patient Demographics' },
      { key: 'visitDate', label: 'Visit date', type: 'date', sub: 'Visit Details' },
      { key: 'visitStart', label: 'Visit start', type: 'time', sub: 'Visit Details' },
      { key: 'visitEnd', label: 'Visit end', type: 'time', sub: 'Visit Details' },
      { key: 'visitType', label: 'Visit type', type: 'select', options: ['Plan of Treatment (POT)', 'Recertification', 'Follow-up'], sub: 'Visit Details' },
      { key: 'certStart', label: 'Cert period start', type: 'date', sub: 'Certification Period' },
      { key: 'certEnd', label: 'Cert period end (60 days)', type: 'date', sub: 'Certification Period' },
      { key: 'recertDate', label: 'Recertification date', type: 'date', sub: 'Certification Period' },
      { key: 'refMD', label: 'Provider name', type: 'text', sub: 'Certifying Provider' },
      { key: 'mdNPI', label: 'Provider NPI', type: 'text', sub: 'Certifying Provider' },
      { key: 'mdCred', label: 'Provider credentials', type: 'text', sub: 'Certifying Provider' },
      { key: 'mdLic', label: 'State license #', type: 'text', sub: 'Certifying Provider' },
    ],
  },
  {
    key: 'diagnoses', title: 'Diagnoses',
    fields: [
      { key: 'dx1', label: 'Primary diagnosis (ICD-10)', type: 'text' },
      { key: 'dx2', label: 'Secondary diagnosis', type: 'text' },
      { key: 'dx3', label: 'Tertiary diagnosis', type: 'text' },
      { key: 'dx4', label: 'Other relevant diagnosis', type: 'text' },
      { key: 'dxNotes', label: 'Additional diagnoses / comorbidities', type: 'textarea' },
    ],
  },
  {
    key: 'vital_signs_physical_exam', title: 'Vital Signs & Physical Exam',
    fields: [
      { key: 'vitalsSet', label: 'Vitals set', type: 'select', options: ['Pre-Infusion', 'During Infusion', 'Post-Infusion'], sub: 'Vital Signs' },
      { key: 'vitalsTime', label: 'Time taken', type: 'time', sub: 'Vital Signs' },
      { key: 'bpS', label: 'BP systolic', type: 'number', sub: 'Vital Signs' },
      { key: 'bpD', label: 'BP diastolic', type: 'number', sub: 'Vital Signs' },
      { key: 'hrV', label: 'Heart rate (bpm)', type: 'number', sub: 'Vital Signs' },
      { key: 'rrV', label: 'Resp. rate', type: 'number', sub: 'Vital Signs' },
      { key: 'tempV', label: 'Temperature (°F)', type: 'number', sub: 'Vital Signs' },
      { key: 'o2V', label: 'O2 saturation (%)', type: 'number', sub: 'Vital Signs' },
      { key: 'wtV', label: 'Weight', type: 'select', options: ['lbs', 'kg'], sub: 'Vital Signs' },
      { key: 'htV', label: 'Height (in)', type: 'number', sub: 'Vital Signs' },
      { key: 'painScale', label: 'Pain score (0–10)', type: 'text', sub: 'Vital Signs' },
      { key: 'painLoc', label: 'Pain location / description', type: 'text', sub: 'Vital Signs' },
      { key: 'genApp', label: 'General appearance', type: 'select', options: ['Select', 'Alert and oriented, no acute distress', 'Alert, mild distress', 'Alert, moderate distress', 'Lethargic', 'Other — see notes'], sub: 'Physical Examination' },
      { key: 'skinS', label: 'Skin', type: 'select', options: ['Select', 'Warm, dry, intact', 'Pallor', 'Diaphoretic', 'Cyanotic', 'Jaundiced', 'Wound / skin alteration present'], sub: 'Physical Examination' },
      { key: 'respS', label: 'Respiratory', type: 'select', options: ['Select', 'Clear to auscultation bilaterally', 'Diminished at bases', 'Wheezing', 'Crackles / rales', 'Labored breathing'], sub: 'Physical Examination' },
      { key: 'cardS', label: 'Cardiovascular', type: 'select', options: ['Select', 'Regular rate and rhythm, no murmur', 'Irregular rhythm', 'Murmur noted', 'Tachycardic', 'Bradycardic'], sub: 'Physical Examination' },
      { key: 'edema', label: 'Edema', type: 'text', sub: 'Physical Examination' },
      { key: 'examNotes', label: 'Additional exam findings', type: 'textarea', sub: 'Physical Examination' },
    ],
  },
  {
    key: 'infusion_drug_order', title: 'Infusion & Drug Order',
    fields: [
      { key: 'drugName', label: 'Drug / biologic name', type: 'text', sub: 'Drug &amp; Order' },
      { key: 'drugBrand', label: 'Brand / biosimilar', type: 'text', sub: 'Drug &amp; Order' },
      { key: 'drugDose', label: 'Ordered dose', type: 'text', sub: 'Drug &amp; Order' },
      { key: 'drugCalc', label: 'Calculated dose (mg)', type: 'text', sub: 'Drug &amp; Order' },
      { key: 'drugConc', label: 'Concentration / volume', type: 'text', sub: 'Drug &amp; Order' },
      { key: 'drugDur', label: 'Infusion duration', type: 'text', sub: 'Drug &amp; Order' },
      { key: 'drugRoute', label: 'Route', type: 'select', options: ['Select', 'Intravenous (IV)', 'Subcutaneous (SubQ)', 'Intramuscular (IM)'], sub: 'Drug &amp; Order' },
      { key: 'infNum', label: 'Infusion # / cycle', type: 'text', sub: 'Drug &amp; Order' },
      { key: 'lastInf', label: 'Last infusion date', type: 'date', sub: 'Drug &amp; Order' },
      { key: 'preMeds', label: 'Pre-medications', type: 'text', sub: 'Drug &amp; Order' },
      { key: 'indication', label: 'Indication / treating condition', type: 'text', sub: 'Drug &amp; Order' },
      { key: 'drugLot', label: 'Drug lot number', type: 'text', sub: 'Drug &amp; Order' },
      { key: 'drugExpDate', label: 'Drug expiration date', type: 'date', sub: 'Drug &amp; Order' },
      { key: 'anaphKitExpDate', label: 'Anaphylaxis kit exp. date', type: 'date', sub: 'Drug &amp; Order' },
      { key: 'accessType', label: 'Access type', type: 'select', options: ['Select', 'Peripheral IV (PIV)', 'PICC line', 'Implanted port', 'Midline catheter', 'CVC', 'SubQ injection site'], sub: 'Vascular Access' },
      { key: 'accessSite', label: 'Site location', type: 'text', sub: 'Vascular Access' },
      { key: 'accessGauge', label: 'Gauge / Fr size', type: 'text', sub: 'Vascular Access' },
      { key: 'accessCond', label: 'Access condition', type: 'text', sub: 'Vascular Access' },
      { key: 'infReact', label: 'Prior infusion reactions', type: 'text', sub: 'Allergies &amp; Reactions' },
    ],
  },
  {
    key: 'medications_reconciliation', title: 'Medications Reconciliation',
    fields: [
      { key: 'adherence', label: 'Adherence concerns', type: 'text' },
      { key: 'ddi', label: 'Drug interactions identified', type: 'text' },
      { key: 'medNotes', label: 'Pharmacist / nursing notes', type: 'textarea' },
    ],
  },
  {
    key: 'mental_status_psychosocial', title: 'Mental Status & Psychosocial',
    fields: [
      { key: 'orient', label: 'Oriented to', type: 'text', sub: 'Cognitive &amp; Mental Status' },
      { key: 'memory', label: 'Memory', type: 'text', sub: 'Cognitive &amp; Mental Status' },
      { key: 'judgment', label: 'Judgment / insight', type: 'text', sub: 'Cognitive &amp; Mental Status' },
      { key: 'commBarrier', label: 'Communication barriers', type: 'text', sub: 'Cognitive &amp; Mental Status' },
      { key: 'moodS', label: 'Mood / affect', type: 'select', options: ['Select', 'Euthymic (normal)', 'Anxious', 'Depressed', 'Irritable', 'Labile', 'Flat / blunted'], sub: 'Cognitive &amp; Mental Status' },
      { key: 'livingS', label: 'Living situation', type: 'select', options: ['Select', 'Lives alone', 'Lives with spouse / partner', 'Lives with family', 'Assisted living', 'Board and care'], sub: 'Cognitive &amp; Mental Status' },
      { key: 'caregiver', label: 'Caregiver', type: 'text', sub: 'Cognitive &amp; Mental Status' },
      { key: 'psychoNotes', label: 'Mental status / psychosocial notes', type: 'textarea', sub: 'Cognitive &amp; Mental Status' },
    ],
  },
  {
    key: 'functional_adl_status', title: 'Functional & ADL Status',
    fields: [
      { key: 'actLevel', label: 'Activity level ordered', type: 'text', sub: 'Activities Permitted' },
      { key: 'exercise', label: 'Exercise / PT', type: 'text', sub: 'Activities Permitted' },
      { key: 'actNotes', label: 'Activity restriction notes', type: 'text', sub: 'Activities Permitted' },
      { key: 'adlNotes', label: 'Functional status notes', type: 'textarea', sub: 'Activities Permitted' },
    ],
  },
  {
    key: 'fall_risk_safety_readmission_risk', title: 'Fall Risk, Safety & Readmission Risk',
    fields: [
      { key: 'fallInterv', label: 'Fall prevention interventions', type: 'text', sub: 'Morse Fall Scale' },
      { key: 'infPrecaution', label: 'Precautions', type: 'text', sub: 'Infection Prevention' },
      { key: 'ivSafety', label: '', type: 'text', sub: 'IV / Access Site Safety' },
      { key: 'readmitRisk', label: 'Risk level', type: 'text', sub: 'ED / Readmission Risk (§10)' },
      { key: 'riskFactors', label: 'Risk factors', type: 'text', sub: 'ED / Readmission Risk (§10)' },
      { key: 'riskInterv', label: 'Interventions', type: 'text', sub: 'ED / Readmission Risk (§10)' },
      { key: 'riskNotes', label: 'Risk assessment notes', type: 'textarea', sub: 'ED / Readmission Risk (§10)' },
      { key: 'fallNotes', label: 'Safety notes', type: 'textarea', sub: 'ED / Readmission Risk (§10)' },
    ],
  },
  {
    key: 'nutritional_status', title: 'Nutritional Status',
    fields: [
      { key: 'appetite', label: 'Appetite', type: 'text' },
      { key: 'wtLoss', label: 'Unintended weight loss (30d)', type: 'text' },
      { key: 'hydration', label: 'Hydration', type: 'text' },
      { key: 'dys', label: 'Dysphagia', type: 'text' },
      { key: 'nausea', label: 'Nausea / vomiting', type: 'text' },
      { key: 'dietType', label: 'Diet type', type: 'select', options: ['Select', 'Regular', 'Soft', 'Low sodium', 'Diabetic / ADA', 'Renal', 'Pureed', 'TPN / tube feeds'] },
      { key: 'fluidRestrict', label: 'Fluid restriction', type: 'text' },
      { key: 'nutriSupp', label: 'Nutritional supplements', type: 'text' },
      { key: 'dietRef', label: 'Dietitian referral', type: 'text' },
      { key: 'nutriNotes', label: 'Nutritional notes', type: 'textarea' },
    ],
  },
  {
    key: 'prognosis_services', title: 'Prognosis & Services',
    fields: [
      { key: 'prognosis', label: 'Prognosis', type: 'text' },
      { key: 'rehabPot', label: 'Rehab potential', type: 'text' },
      { key: 'progBasis', label: 'Clinical basis for prognosis', type: 'textarea' },
      { key: 'funcOutcomes', label: 'Expected functional outcomes', type: 'textarea' },
      { key: 'orderedSvc', label: 'Ordered services', type: 'text' },
      { key: 'dmeNotes', label: 'DME / equipment', type: 'textarea' },
    ],
  },
  {
    key: 'education_plan', title: 'Education Plan',
    fields: [
      { key: 'eduTo', label: 'Education delivered to', type: 'text' },
      { key: 'eduLang', label: 'Primary language', type: 'select', options: ['English', 'Spanish', 'Vietnamese', 'Korean', 'Tagalog', 'Other'] },
      { key: 'dischNotes', label: 'Care planning notes', type: 'textarea' },
    ],
  },
  {
    key: 'advanced_directives', title: 'Advanced Directives',
    fields: [
      { key: 'advDir', label: 'Advanced directive on file', type: 'text' },
      { key: 'dirType', label: 'Type of directive', type: 'text' },
      { key: 'dirLoc', label: 'Location of directive', type: 'text' },
      { key: 'codeStatus', label: 'Code status', type: 'text' },
      { key: 'dirNotes', label: 'Directive notes', type: 'textarea' },
    ],
  },
  {
    key: 'goals_frequency_orders', title: 'Goals, Frequency & Orders',
    fields: [
      { key: 'visitFreq', label: 'Visit frequency', type: 'select', options: ['Select', 'Every 2 weeks', 'Every 4 weeks', 'Every 6 weeks', 'Every 8 weeks', 'Every 12 weeks'], sub: 'Visit Frequency' },
      { key: 'careDur', label: 'Duration of care', type: 'select', options: ['Select', '60 days', '90 days', '6 months', '12 months', 'Indefinite per MD order'], sub: 'Visit Frequency' },
      { key: 'nextInfDate', label: 'Next infusion date', type: 'date', sub: 'Visit Frequency' },
      { key: 'nextRecert', label: 'Next recert due', type: 'date', sub: 'Visit Frequency' },
      { key: 'freqRationale', label: 'Frequency rationale', type: 'textarea', sub: 'Visit Frequency' },
      { key: 'vo1Date', label: 'Order date *', type: 'date', sub: 'Verbal Orders' },
      { key: 'vo1By', label: 'Order received by *', type: 'text', sub: 'Verbal Orders' },
      { key: 'vo1MD', label: 'Provider authorizing *', type: 'text', sub: 'Verbal Orders' },
      { key: 'vo1Type', label: 'Order type *', type: 'select', options: ['Select order type', 'Medication Order', 'Lab Order', 'Nursing Order', 'Other'], sub: 'Verbal Orders' },
      { key: 'vo1ReadBack', label: 'Read-back performed', type: 'checkbox', sub: 'Verbal Orders' },
      { key: 'vo1Verified', label: 'Verified against patient profile', type: 'checkbox', sub: 'Verbal Orders' },
      { key: 'vo1Cosign', label: 'Provider cosignature date', type: 'date', sub: 'Verbal Orders' },
    ],
  },
  {
    key: 'attestation_signatures', title: 'Attestation & Signatures',
    fields: [
      { key: 'nurseName', label: 'Nurse name', type: 'text', sub: 'Nurse Attestation' },
      { key: 'nurseCred', label: 'Credentials', type: 'select', options: ['RN', 'RN, BSN', 'RN, BSN, CRNI', 'LVN', 'PharmD, RPh'], sub: 'Nurse Attestation' },
      { key: 'nurseLic', label: 'CA license #', type: 'text', sub: 'Nurse Attestation' },
      { key: 'admitDate', label: 'Date of visit', type: 'date', sub: 'Nurse Attestation' },
      { key: 'sigCanvas', label: 'Nurse signature (sign below)', type: 'text', sub: 'Nurse Attestation' },
      { key: 'physName', label: 'Physician name', type: 'text', sub: 'Physician Certification (to be completed after printing)' },
      { key: 'physNPI', label: 'Credentials / NPI', type: 'text', sub: 'Physician Certification (to be completed after printing)' },
      { key: 'physPhone', label: 'Phone', type: 'text', sub: 'Physician Certification (to be completed after printing)' },
      { key: 'physFax', label: 'Fax', type: 'text', sub: 'Physician Certification (to be completed after printing)' },
      { key: 'physLic', label: 'State license #', type: 'text', sub: 'Physician Certification (to be completed after printing)' },
      { key: 'physician_signature', label: 'Physician signature', type: 'text', sub: 'Physician Certification (to be completed after printing)' },
      { key: 'physSignDate', label: 'Date signed', type: 'date', sub: 'Physician Certification (to be completed after printing)' },
    ],
  },
]

export const TOTAL_STEPS = PLAN_OF_CARE_STEPS.length // 14
